import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Building2, Award, TrendingUp, Heart } from 'lucide-react';
import { heroStats, trustBar, painPoints, services, methodSteps, caseStudies, images } from '../data/mock';
import { PrimaryButton, SectionLabel, Stat } from '../components/Shared';

// Audience-segmentation paths — rendered high on the home page so visitors
// can self-select their journey within seconds. Route each persona to its
// dedicated landing.
const AUDIENCE_PATHS = [
  {
    persona: 'אני מנכ״ל / חברת B2B',
    icon: Building2,
    tag: 'B2B · תעשייה',
    desc: 'אבחון מערכות ואסטרטגיית צמיחה לחברות הנדסה, תעשייה ו-B2B מורכב.',
    to: '/ceo',
    cta: 'לדף הנחיתה של B2B',
    testId: 'audience-ceo',
  },
  {
    persona: 'אני מומחה עם שיטה',
    icon: Award,
    tag: 'מומחים · חזון',
    desc: 'בניית אדריכלות עסקית שהופכת מומחיות נדירה למכונת צמיחה שיטתית.',
    to: '/experts',
    cta: 'לדף הנחיתה של המומחים',
    testId: 'audience-experts',
  },
  {
    persona: 'אני משקיע פוטנציאלי',
    icon: TrendingUp,
    tag: 'משקיעים',
    desc: 'הזדמנויות ב״נדל״ן דיגיטלי״ — מודל השקעה עם תשואה חזויה ושותפות ארוכת טווח.',
    to: '/investors',
    cta: 'לדף הנחיתה למשקיעים',
    testId: 'audience-investors',
  },
  {
    persona: 'אני עמותה / קהילה',
    icon: Heart,
    tag: 'מוסדות · Zchuta',
    desc: 'מנוע גיוס תרומות דיגיטלי למוסדות, קהילות ובתי כנסת — מודל Success-based.',
    to: '/zchuta',
    cta: 'לדף הנחיתה של זכותא',
    testId: 'audience-zchuta',
  },
];

const Home = () => {
  const [activePain, setActivePain] = useState(null);

  return (
    <>
      {/* HERO */}
      <section className="relative pt-32 pb-12 lg:pt-44 lg:pb-20 overflow-hidden hero-backdrop">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 reveal">
              <SectionLabel>אסטרטגיה · דיגיטל · צמיחה</SectionLabel>
              <h1 className="display-1 text-navy text-balance">
                העסק עובד, הלקוחות נאמנים, והרווחיות יציבה.<br />
                עכשיו חסרה רק <span className="accent-underline">מערכת אחת</span> שתהפוך את הדיגיטל שלכם
                למנוע צמיחה אמיתי.
              </h1>
              <p className="lede text-navy/70 text-lg lg:text-xl mt-8 max-w-2xl">
                עסק פעיל ורווחי לא יכול להרשות לעצמו שיווק מפוזר, ספקים שלא מתקשרים ביניהם,
                ודוחות שמראים &ldquo;קליקים&rdquo; במקום תוצאות בשטח. Gal&Blizovsky מרכזת ומנהלת את כל
                מערך השיווק והדיגיטל שלכם תחת קורת גג אחת, אסטרטגיה אחת ויעד אחד: גדילה
                חזקה במכירות ובלידים האיכותיים.
              </p>
              <p className="text-navy/60 text-base lg:text-lg mt-6 max-w-2xl">
                אתם יודעים שהדיגיטל הוא המקום להיות בו כדי לצמוח, אבל השוק מוצף באנשי
                מקצוע, מושגים טכניים ופתרונות חלקיים. במקום לנהל סוללת ספקים ולקבל ביצועים
                בינוניים, אנחנו מזמינים אתכם להעביר את המושכות לגוף אחד אמין, איכותי ובעל
                סמכות, שיודע לתכלל את כל הפעילות למערכת צמיחה שעובדת עבורכם.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-12">
                <PrimaryButton to="/contact">קבעו שיחת אבחון לבניית מערך צמיחה</PrimaryButton>
                <PrimaryButton to="#pain" variant="outline">
                  גלו למה השיווק שלכם עובד פחות ממה שהוא מסוגל
                </PrimaryButton>
              </div>
            </div>
            <div className="lg:col-span-5 reveal">
              <div className="image-overlay aspect-[4/5] bg-navy-100">
                <img
                  src={images.hero}
                  alt=""
                  role="presentation"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            </div>
          </div>

          {/* Stat strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10 mt-20 lg:mt-28 reveal">
            {heroStats.map((s) => (
              <Stat key={s.label} {...s} />
            ))}
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-navy text-cream py-6 overflow-hidden" aria-label="פס אמון">
        <div className="flex whitespace-nowrap marquee-track">
          {[...trustBar, ...trustBar, ...trustBar].map((item, i) => (
            <span key={`${item}-${i}`} className="flex items-center gap-12 px-6 text-sm text-cream/80 tracking-wide">
              <span>{item}</span>
              <span className="text-bronze" aria-hidden="true">—</span>
            </span>
          ))}
        </div>
      </section>

      {/* AUDIENCE SEGMENTATION — self-select your path */}
      <section
        className="py-20 lg:py-24 bg-cream"
        aria-labelledby="audience-heading"
        data-testid="audience-segmentation"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-14 max-w-3xl mx-auto reveal">
            <SectionLabel>בחירת מסלול</SectionLabel>
            <h2 id="audience-heading" className="display-2 text-navy mt-4">
              מי אתם?<br className="hidden md:block" />
              <span className="text-bronze">נכוון אתכם לדף הנכון.</span>
            </h2>
            <p className="text-navy/65 text-base lg:text-lg mt-5 leading-relaxed">
              לכל קהל יעד יש מסלול, שפה ותהליך מותאמים. בחרו את מה שמייצג אתכם ונביא אתכם ישר לפגישה הנכונה.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {AUDIENCE_PATHS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Link
                  key={p.to}
                  to={p.to}
                  className="group relative bg-white border border-navy/10 p-7 lg:p-8 flex flex-col h-full lift-card reveal focus:outline-none focus-visible:ring-2 focus-visible:ring-bronze focus-visible:ring-offset-2"
                  style={{ transitionDelay: `${i * 80}ms` }}
                  data-testid={p.testId}
                  aria-label={`${p.persona} — ${p.cta}`}
                >
                  {/* Tag */}
                  <span className="text-[10px] tracking-[0.18em] uppercase font-semibold text-bronze mb-5">
                    {p.tag}
                  </span>

                  {/* Icon */}
                  <div className="w-12 h-12 bg-navy/5 flex items-center justify-center mb-5 group-hover:bg-navy group-hover:text-cream transition-colors">
                    <Icon className="w-6 h-6 text-navy group-hover:text-cream" aria-hidden="true" />
                  </div>

                  {/* Persona */}
                  <h3 className="font-serif text-xl lg:text-2xl text-navy mb-3 leading-snug">
                    {p.persona}
                  </h3>

                  {/* Description */}
                  <p className="text-navy/65 text-sm leading-relaxed flex-1">{p.desc}</p>

                  {/* CTA arrow */}
                  <div className="mt-6 pt-4 border-t border-navy/10 flex items-center justify-between text-sm">
                    <span className="text-navy font-medium">{p.cta}</span>
                    <ArrowLeft
                      className="w-4 h-4 text-bronze transition-transform duration-300 group-hover:-translate-x-1"
                      aria-hidden="true"
                    />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* MIRROR / EMPATHY */}
      <section className="py-24 lg:py-32 bg-cream" aria-labelledby="mirror-heading">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <div className="lg:col-span-5 reveal">
              <div className="image-overlay aspect-[4/5] bg-navy-100 lg:sticky lg:top-32">
                <img src={images.whatWeDo} alt="" role="presentation" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="lg:col-span-7 reveal">
              <SectionLabel>השיקוף</SectionLabel>
              <h2 id="mirror-heading" className="display-2 text-navy mb-10">
                יש לכם מוצר מצוין, קהל שאוהב אתכם ושולי רווח בריאים. אז למה כל כך קשה
                לפרוץ את <span className="text-bronze">תקרת הזכוכית</span> בדיגיטל?
              </h2>
              <div className="space-y-6 text-navy/75 lede">
                <p>
                  הרבה בעלי עסקים מצליחים מוצאים את עצמם מתוסכלים כשהם מגיעים לדיגיטל.
                  יש להם קמפיינר שאחראי על המודעות, משרד שבנה את האתר, אולי גם איש תוכן
                  או SEO. כל אחד עובד בגזרה שלו, שולח בסוף החודש דוח יפה עמוס בנתונים
                  משעממים על &ldquo;חשיפות&rdquo; או &ldquo;אחוזי הקלקה&rdquo; — אבל כשמסתכלים על השורה התחתונה,
                  המכירות לא מזנקות באותו קצב.
                </p>
                <p>
                  הבלבול הזה טבעי. השוק מלא בסוגים שונים של מומחים, ולעסק שרוצה לגדול אין
                  זמן (או רצון) להפוך למנהל שיווק שמתאם בין כולם.
                </p>
                <p>
                  כשהפעילות מפוזרת, לידים הולכים לאיבוד, פניות מגיעות לא בשלות, והתקציב
                  נשחק על ניסוי וטעייה. הבעיה שלכם היא לא שחסר לכם שיווק; הבעיה היא שחסר
                  לכם <strong className="text-navy">ניהול שיווקי</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE UNIQUE MECHANISM — dark section */}
      <section className="dark-section py-24 lg:py-32 relative overflow-hidden" aria-labelledby="mechanism-heading">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 reveal">
              <p className="section-label mb-6" style={{ color: 'rgba(250,250,247,0.6)' }}>המנגנון</p>
              <h2 id="mechanism-heading" className="display-2 text-cream mb-10">
                מפסיקים לאלתר.<br />
                מתחילים לנהל צמיחה <span className="text-bronze">כסטנדרט הנדסי</span>.
              </h2>
              <div className="space-y-6 lede text-cream/75">
                <p>
                  כאן בדיוק נכנסת Gal&Blizovsky. אנחנו לא מציעים לכם &ldquo;עוד מאותו דבר&rdquo; ולא
                  מוכרים לכם פתרון מדף של קמפיין נקודתי.
                </p>
                <p>
                  אנחנו מתפקדים כזרוע השיווקית-אסטרטגית המלאה שלכם: מספק אחד אמין ואיכותי,
                  אתם מקבלים מעטפת שלמה שמתכללת את כל הפעילות. מניתוח העסק וקהל היעד, דרך
                  תכנון האסטרטגיה, ועד ניהול הספקים, בניית התשתיות, הזרמת הלידים האיכותיים,
                  והבאת לקוחות בשלים עד לצוות המכירות שלכם. תכלס, תוצאות בשטח.
                </p>
              </div>
              <div className="mt-10">
                <PrimaryButton to="/services" variant="bronze">לכל השירותים</PrimaryButton>
              </div>
            </div>
            <div className="lg:col-span-5 reveal">
              <div className="image-overlay aspect-square">
                <img src={images.philosophy} alt="" role="presentation" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PAIN POINTS — interactive (3 focused) */}
      <section
        id="pain"
        className="py-24 lg:py-32 bg-cream-200"
        aria-labelledby="pain-heading"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-16 reveal">
            <SectionLabel>איפה צוואר הבקבוק שלכם?</SectionLabel>
            <h2 id="pain-heading" className="display-2 text-navy mb-6">
              מה עוצר את המכירות שלכם מלהכפיל את עצמן?
            </h2>
            <p className="lede text-navy/70 text-lg">
              בחרו את האתגר המרכזי שמתסכל אתכם כרגע, ונראה לכם איך המערכת שלנו פותרת
              אותו מהשורש.
            </p>
          </div>

          <div className="space-y-3 reveal" role="region" aria-label="שלושה אתגרים שיווקיים מרכזיים">
            {painPoints.map((pp) => {
              const isOpen = activePain?.id === pp.id;
              const panelId = `pain-panel-${pp.id}`;
              const buttonId = `pain-btn-${pp.id}`;
              return (
                <div
                  key={pp.id}
                  id={`pain-${pp.id}`}
                  className={`bg-cream border-2 transition-all duration-500 ${
                    isOpen ? 'border-navy shadow-lg' : 'border-transparent hover:border-navy/30'
                  }`}
                >
                  <button
                    type="button"
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => {
                      const next = isOpen ? null : pp;
                      setActivePain(next);
                      if (next) {
                        setTimeout(() => {
                          const el = document.getElementById(`pain-${pp.id}`);
                          if (el) {
                            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }, 50);
                      }
                    }}
                    className="w-full text-right p-7 lg:p-9 flex items-start justify-between gap-6"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-3">
                        <span className="font-serif text-bronze text-2xl" aria-hidden="true">
                          {pp.id.toString().padStart(2, '0')}
                        </span>
                        <span className="eyebrow text-bronze">אתגר {pp.id}</span>
                      </div>
                      <h3 className="display-3 text-navy text-balance">{pp.title}</h3>
                      {!isOpen && <p className="text-navy/60 text-sm mt-3">{pp.short}</p>}
                    </div>
                    <span
                      aria-hidden="true"
                      className={`flex-shrink-0 w-12 h-12 border border-navy/20 flex items-center justify-center transition-all duration-500 ${
                        isOpen ? 'bg-navy text-cream rotate-45 border-navy' : 'text-navy'
                      }`}
                    >
                      <Plus className="w-5 h-5" strokeWidth={1.5} />
                    </span>
                  </button>

                  {/* Expanded response */}
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    aria-live="polite"
                    className={`overflow-hidden transition-all duration-700 ease-out ${
                      isOpen ? 'max-h-[1200px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-7 lg:px-9 pb-9 pt-2">
                      <div className="grid lg:grid-cols-12 gap-8 pt-8 border-t border-navy/15">
                        <div className="lg:col-span-2">
                          <p className="eyebrow text-bronze">תשובת Gal&Blizovsky</p>
                        </div>
                        <div className="lg:col-span-10">
                          {pp.response.split('\n\n').map((p, i) => (
                            <p key={`${pp.id}-p-${i}`} className="lede text-navy/80 mb-5 last:mb-7 text-base lg:text-lg">
                              {p}
                            </p>
                          ))}
                          <Link
                            to="/contact"
                            className="btn-cta inline-flex items-center gap-3 bg-navy text-cream px-6 py-4 text-sm tracking-wide hover:bg-bronze hover:text-navy transition-colors duration-300"
                          >
                            {pp.cta}
                            <ArrowLeft className="arrow w-4 h-4" aria-hidden="true" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-24 lg:py-32 bg-cream" aria-labelledby="services-preview-heading">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8 mb-16 reveal">
            <div className="max-w-2xl">
              <SectionLabel>השירותים שלנו</SectionLabel>
              <h2 id="services-preview-heading" className="display-2 text-navy">
                כל מה שהעסק צריך כדי לגדול — אצל ספק אסטרטגי אחד.
              </h2>
            </div>
            <PrimaryButton to="/services" variant="outline">כל השירותים</PrimaryButton>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-navy/10 reveal">
            {services.map((s) => (
              <div key={s.n} className="bg-cream p-8 lg:p-10 lift-card">
                <p className="font-serif text-bronze text-3xl mb-4" aria-hidden="true">{s.n}</p>
                <h3 className="display-3 text-navy mb-4">{s.title}</h3>
                <p className="text-navy/60 italic mb-4 text-sm">{s.sub}</p>
                <p className="text-navy/75 text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* METHOD PREVIEW */}
      <section className="py-24 lg:py-32 bg-cream-200" aria-labelledby="method-preview-heading">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-16 reveal">
            <SectionLabel>השיטה</SectionLabel>
            <h2 id="method-preview-heading" className="display-2 text-navy">
              לא אלתור, אלא <span className="text-bronze">הנדסת תהליכים</span>.
            </h2>
          </div>
          <div className="grid lg:grid-cols-4 gap-x-12 gap-y-16 reveal">
            {methodSteps.map((step) => (
              <div key={step.n} className="border-t border-navy/15 pt-6">
                <p className="font-serif text-3xl text-bronze mb-4" aria-hidden="true">{step.n}</p>
                <h3 className="display-3 text-navy mb-4">{step.title}</h3>
                <p className="text-navy/70 text-sm leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-16 reveal">
            <PrimaryButton to="/method">לקרוא על השיטה המלאה</PrimaryButton>
          </div>
        </div>
      </section>

      {/* CASE STUDIES PREVIEW */}
      <section className="py-24 lg:py-32 bg-cream" aria-labelledby="cases-preview-heading">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8 mb-16 reveal">
            <div>
              <SectionLabel>מקרי בוחן</SectionLabel>
              <h2 id="cases-preview-heading" className="display-2 text-navy">
                תוצאות שמדברות בעד עצמן.
              </h2>
            </div>
            <PrimaryButton to="/results" variant="outline">כל התוצאות</PrimaryButton>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 reveal">
            {caseStudies.map((c) => (
              <article key={c.name} className="lift-card border border-navy/10 p-8 lg:p-10 bg-cream">
                <p className="num-callout text-bronze mb-6" aria-hidden="true">{c.metric}</p>
                <h3 className="display-3 text-navy mb-3">{c.name}</h3>
                <p className="font-serif text-navy/80 mb-5 text-lg">{c.headline}</p>
                <p className="text-navy/70 text-sm leading-relaxed">{c.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
